/**
 * Analytics `GET /members/{id}/detail` (via CM-API POST) → Member 360 Overview.
 * Hierarchy owns identity/money; analytics overlays period + daily trend.
 */
import { fromMinorUnits } from '@/features/hierarchy/hierarchy'
import type { MemberDetailView } from '@/features/member/memberAccount'

function fromMinorUnitsSigned(minor: string | null | undefined): number {
  if (minor == null || minor === '') return 0
  const n = Number(minor)
  return Number.isFinite(n) ? n / 100 : 0
}

/** analytics-service member detail body (OpenAPI). */
export type AnalyticsMemberDetail = {
  asOf?: string
  martBuiltAt?: string | null
  martStale?: boolean
  memberId?: string
  name?: string
  parentId?: string
  createdAt?: string
  creditLimitMinor?: string
  balanceMinor?: string
  exposure?: {
    valueMinor?: string | null
    stale?: boolean
  }
  period?: {
    turnoverMinor?: string
    betsPlaced?: number
    betsSettled?: number
    memberNetMinor?: string
    bookNetMinor?: string
  } | null
  trend?: Array<{
    date: string
    betsPlaced?: number
    turnoverMinor?: string
    betsSettled?: number
    bookNetMinor?: string
    activeMembers?: number
  }>
  lastBetDate?: string | null
  hasEverSignedIn?: boolean | null
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v)
}

/** Peel analytics payload from CM unwrap or raw upstream. */
export function asAnalyticsMemberDetail(live: unknown): AnalyticsMemberDetail | null {
  if (!isRecord(live)) return null
  // Soft-empty `{ period: {} }` still counts — trend/period keys may arrive later.
  if (
    live.period !== undefined ||
    live.trend !== undefined ||
    live.balanceMinor !== undefined ||
    live.memberId !== undefined
  ) {
    return live as AnalyticsMemberDetail
  }
  return null
}

/**
 * Analytics mock / soft stub: `Member ${uuid.slice(0, 8)}`.
 * Must not overwrite hierarchy login username (Lab / Agent: boplayer1).
 */
export function isAnalyticsMemberNameStub(name: string): boolean {
  return /^Member\s+[0-9a-f]{8}$/i.test(name.trim())
}

/**
 * Overlay analytics onto hierarchy MemberDetailView.
 * Keeps hierarchy balance/credit/exposure unless analytics exposure is fresh.
 */
export function mergeAnalyticsMemberDetail(
  base: MemberDetailView,
  live: AnalyticsMemberDetail,
): MemberDetailView {
  const period = live.period ?? null
  const turnover = fromMinorUnits(period?.turnoverMinor)
  const memberPnl = fromMinorUnitsSigned(period?.memberNetMinor)
  const betsPlaced = period?.betsPlaced ?? 0
  const betsSettled = period?.betsSettled ?? 0

  const daily = (live.trend ?? []).map((d) => ({
    date: d.date,
    turnover: fromMinorUnits(d.turnoverMinor),
    memberPnl: fromMinorUnitsSigned(d.bookNetMinor) * -1,
    bets: d.betsSettled ?? d.betsPlaced ?? 0,
  }))

  const exposureFresh =
    live.exposure &&
    !live.exposure.stale &&
    live.exposure.valueMinor != null &&
    live.exposure.valueMinor !== ''
  const openExposure = exposureFresh
    ? Math.max(0, fromMinorUnitsSigned(live.exposure!.valueMinor))
    : base.openExposure

  const analyticsName =
    typeof live.name === 'string' && live.name.trim() ? live.name.trim() : ''
  const useAnalyticsName = Boolean(analyticsName) && !isAnalyticsMemberNameStub(analyticsName)

  const account = {
    ...base.account,
    ...(useAnalyticsName ? { username: analyticsName, displayName: analyticsName } : {}),
    ...(typeof live.createdAt === 'string' && live.createdAt
      ? { createdAt: live.createdAt }
      : {}),
    ...(live.lastBetDate
      ? { lastActiveAt: `${live.lastBetDate}T00:00:00.000Z` }
      : {}),
    ...(exposureFresh ? { exposure: openExposure } : {}),
  }

  const hasPeriod =
    period != null &&
    (Number(period.turnoverMinor ?? 0) !== 0 ||
      Number(period.memberNetMinor ?? 0) !== 0 ||
      betsPlaced > 0 ||
      betsSettled > 0)

  return {
    ...base,
    account,
    openExposure,
    daily: daily.length > 0 ? daily : base.daily,
    lifetime: hasPeriod
      ? {
          ...base.lifetime,
          turnover: Number.isFinite(turnover) ? turnover : 0,
          memberPnl: Number.isFinite(memberPnl) ? memberPnl : 0,
          bets: betsPlaced,
          settledBets: betsSettled,
          avgStake: betsSettled > 0 && Number.isFinite(turnover) ? turnover / betsSettled : 0,
        }
      : base.lifetime,
  }
}
